import { Component, computed, DestroyRef, effect, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { interval, tap } from 'rxjs';
import { AppContext } from 'src/app/app/context';
import { logger } from 'src/app/pbir/utils/logger';
import { SpendETLStatus } from 'src/app/shared/models/etl-status';
import { VolatileSettingsDataService } from '../../../../../services/data/volatile-settings-data.service';
import { EtlBannerDisplaySettings } from '../../../../models/etl-banner-display-settings';

const BANNER_CHECK_INTERVAL = 2000;
const BODY_CLASS = 'snack-info-shifted';

const LOGGER = logger('FooterEtlStatusBannerComponent');

@Component({
  selector: 'app-footer-etl-status-banner',
  templateUrl: './footer-etl-status-banner.component.html',
  styleUrls: ['./footer-etl-status-banner.component.scss'],
  standalone: false
})
export class FooterEtlStatusBannerComponent {
  readonly etlStatusBanner = inject(EtlStatusBanner);
  readonly destroyRef = inject(DestroyRef);
  readonly dataHealthService = inject(VolatileSettingsDataService);
  readonly settings = this.etlStatusBanner.settings;

  constructor() {
    effect(() => {
      let bannerDismissed = !!this.settings().bannerDismissed;
      document.body.classList.toggle(BODY_CLASS, bannerDismissed);
    });
  }

  ngOnDestroy() {
    document.body.classList.remove(BODY_CLASS);
  }

  dismiss(): void {
    document.body.classList.remove(BODY_CLASS);

    this.dataHealthService.setDataHealthBannerDismissed(true);
  }
}

// TODO: Move into own file
const EtlStatusBannerStorageKeyPrefix = `Insights:EtlStatusBannerStorageKey`;

@Injectable({ providedIn: 'root' })
export class EtlStatusBanner {
  readonly destroyRef = inject(DestroyRef);
  readonly appContext = inject(AppContext);

  readonly storageKey = computed(() => {
    let user = this.appContext.user();
    if (!user)
      return null;

    return `${EtlStatusBannerStorageKeyPrefix}:${user.organisationId}:${user.id}`;
  });

  private readonly _settings = signal<EtlBannerDisplaySettings>(null);

  readonly settings = computed(() => this._settings() ?? {
    spendETLEnabled: false,
    etlRunId: 0,
    etlStatus: SpendETLStatus.Unknown,
    bannerDismissed: true,
    title: '',
    message: '',
    organisationId: this.appContext.user().organisationId,
    showDataHealth: false
  });

  constructor() {
    this._settings.set(this.loadSettings());

    interval(BANNER_CHECK_INTERVAL).pipe(
      takeUntilDestroyed(this.destroyRef),
      tap(() => this._settings.set(this.loadSettings()))
    ).subscribe();
  }

  updateSettings(callback: (value: EtlBannerDisplaySettings) => EtlBannerDisplaySettings) {
    this.setSettings(callback(this.loadSettings()));
  }

  setSettings(value: EtlBannerDisplaySettings) {
    try {
      sessionStorage.setItem(this.storageKey(), JSON.stringify(value));
    }
    catch (err) {
      LOGGER.error(`Could not set EtlBannerDisplaySettings in sessionStorage`, err);
    }
    finally {
      this._settings.set(value);
    }
  }

  clearSettings() {
    try {
      sessionStorage.removeItem(this.storageKey());
    }
    catch (err) {
      LOGGER.error(`Could not clear EtlBannerDisplaySettings in sessionStorage`, err);
    }
    finally {
      this._settings.set(null);
    }
  }

  private loadSettings(): EtlBannerDisplaySettings {
    try {
      let value = sessionStorage.getItem(this.storageKey());
      if (value) 
        return JSON.parse(value);
    }
    catch (err) {
      LOGGER.error(`Could not load EtlBannerDisplaySettings from sessionStorage`, err);
    }

    return null;
  }
}
